import { AddressType, LoadingStatusType, TypeComponentNoti } from "@/enums/enums";
import {
  GetMerchantTemplateDefault,
  SetCreateMerchantModel,
  SetMerchantCreateTemplateBySearchedMerchantAddress,
} from "@/helper/merchantHelper";
import { GetAppConnectErrorMessageByExceptionCode, GetMessageExceptionCode } from "@/helper/shareHelper";
import { i18n } from "@/i18n";
import { ToSearchedContactAddress } from "@/service/contact/contactFacade";
import API from "@/service/Index";
import { ConvertToUserModel } from "@/service/user/authenFacade";
import { Action, Module, Mutation, VuexModule } from "vuex-module-decorators";
import store from "..";

@Module({ namespaced: true })
export default class Merchant extends VuexModule {
  private merchantTemplate: any = GetMerchantTemplateDefault();
  private merchantId = 0;
  private insertMerchantLoadingStatus: LoadingStatusType = LoadingStatusType.None;
  private searchedMerchantAddressLoadingStatus: LoadingStatusType = LoadingStatusType.None;
  private merchantListLoadingStatus: LoadingStatusType = LoadingStatusType.None;
  private merchantList: Array<any> | null = null;
  private verificationMerchantLoadingStatus: LoadingStatusType = LoadingStatusType.None;
  private verificationMerchantByMatchLinkLoadingStatus: LoadingStatusType = LoadingStatusType.None;

  get getMerchantTemplate(): any {
    return this.merchantTemplate;
  }

  get getMerchantIdByInsertMerchant(): number {
    return this.merchantId;
  }

  get getInsertMerchantLoadingStatus(): LoadingStatusType {
    return this.insertMerchantLoadingStatus;
  }

  get getSearchedMerchantAddressLoadingStatus(): LoadingStatusType {
    return this.searchedMerchantAddressLoadingStatus;
  }

  get getMerchantListLoadingStatus(): LoadingStatusType {
    return this.merchantListLoadingStatus;
  }

  get getMerchantList(): any {
    return this.merchantList;
  }

  get getVerificationMerchantLoadingStatus(): LoadingStatusType {
    return this.verificationMerchantLoadingStatus;
  }

  get getVerificationMerchantByMatchLinkLoadingStatus(): LoadingStatusType {
    return this.verificationMerchantByMatchLinkLoadingStatus;
  }

  @Mutation
  public SetMerchantTemplate(merchantTemplate: any) {
    this.merchantTemplate = merchantTemplate;
  }

  @Mutation
  public SetMerchantId(merchantId: number) {
    this.merchantId = merchantId;
  }

  @Mutation
  public SetInsertMerchantLoadingStatus(status: LoadingStatusType) {
    this.insertMerchantLoadingStatus = status;
  }

  @Mutation
  public SetGetSearchedMerchantAddressLoadingStatus(status: LoadingStatusType) {
    this.searchedMerchantAddressLoadingStatus = status;
  }

  @Mutation
  public SetMerchantListLoadingStatus(status: LoadingStatusType) {
    this.merchantListLoadingStatus = status;
  }

  @Mutation
  public SetMerchantList(merchantList: Array<any>) {
    this.merchantList = merchantList;
  }

  @Mutation
  public SetVerificationMerchantLoadingStatus(status: LoadingStatusType) {
    this.verificationMerchantLoadingStatus = status;
  }

  @Mutation SetVerificationMerchantByMatchLinkLoadingStatus(status: LoadingStatusType) {
    this.verificationMerchantByMatchLinkLoadingStatus = status;
  }

  @Action
  public DoInsertMerchant(createMerchantTemplate: any) {
    if (this.insertMerchantLoadingStatus !== LoadingStatusType.Loading) {
      this.context.commit("SetInsertMerchantLoadingStatus", LoadingStatusType.Loading);
      const createMerchantModel = SetCreateMerchantModel(createMerchantTemplate.merchantTemplate);
      const input = {
        createMerchantModel: createMerchantModel,
        userEmail: createMerchantTemplate.userModel.userEmail,
      };
      API.MerchantService.DoInsertMerchant(input).then((res) => {
        if (res.data.responseCode.IsSuccess()) {
          API.UserService.DoUpdateReferFriendStatusCaseCreateMerchant(res.data.merchantId);
          store.dispatch("LogIn/DoRefreshMerchantList", { root: true }).then((response) => {
            if (response) {
              store.dispatch("Merchant/DoUpdateMerchantList", { root: true });
              this.context.commit("SetMerchantId", res.data.merchantId);
              this.context.commit("SetInsertMerchantLoadingStatus", LoadingStatusType.Success);
            }
          });
        } else {
          this.context.dispatch(
            "Alert/SetAlertMessage",
            {
              alertMessage: i18n.t("Notification_Verified_Merchant"),
              typeMessage: TypeComponentNoti.Error,
            },
            { root: true }
          );
          this.context.commit("SetInsertMerchantLoadingStatus", LoadingStatusType.None);
        }
      });
    }
  }

  @Action
  public DoUpdateMerchantList() {
    if (this.merchantListLoadingStatus !== LoadingStatusType.Loading) {
      this.context.commit("SetMerchantListLoadingStatus", LoadingStatusType.Loading);
      API.AuthenService.DoGetUserInfo().then((res) => {
        if (res.status.IsSuccess()) {
          const userModel = ConvertToUserModel(res.data.userInfo);
          this.context.commit("SetMerchantList", userModel);
        }
        this.context.commit("SetMerchantListLoadingStatus", LoadingStatusType.Success);
      });
    }
  }

  @Action DoResetMerchantTemplate() {
    this.context.commit("SetMerchantTemplate", GetMerchantTemplateDefault());
    this.context.commit("SetMerchantId", 0);
    this.context.commit("SetInsertMerchantLoadingStatus", LoadingStatusType.None);
  }

  @Action
  public DoGetSearchedMerchantAddress(merchantTemplate: any) {
    if (this.searchedMerchantAddressLoadingStatus !== LoadingStatusType.Loading) {
      this.context.commit("SetGetSearchedMerchantAddressLoadingStatus", LoadingStatusType.Loading);
      const input = {
        taxId: merchantTemplate.taxId.value,
        branchCode: merchantTemplate.branchCode.value ? merchantTemplate.branchCode.value : "00000",
      };
      API.ContactService.DoGetSearchedContactAddress(input).then((res) => {
        if (res.data.responseCode.IsSuccess()) {
          const searchedAddress = ToSearchedContactAddress(res.data.searchedAddress);
          if (searchedAddress.addressType !== AddressType.NoAddress) {
            const createMerchantTemplate = SetMerchantCreateTemplateBySearchedMerchantAddress(
              merchantTemplate,
              searchedAddress
            );
            this.context.commit("SetMerchantTemplate", createMerchantTemplate);
          } else {
            this.context.commit(
              "SetMerchantTemplate",
              GetMerchantTemplateDefault(searchedAddress.taxId, searchedAddress.branchCode)
            );
          }
        }
        this.context.commit("SetGetSearchedMerchantAddressLoadingStatus", LoadingStatusType.Success);
      });
    }
  }

  @Action
  public DoVerificationMerchant(input: any) {
    if (this.verificationMerchantLoadingStatus !== LoadingStatusType.Loading) {
      this.context.dispatch("Loading/IsLoading", true, { root: true });
      this.context.commit("SetVerificationMerchantLoadingStatus", LoadingStatusType.Loading);
      API.MerchantService.DoVerificationMerchant(input).then((res) => {
        if (res.data.responseCode.IsSuccess()) {
          this.context.commit("SetVerificationMerchantLoadingStatus", LoadingStatusType.Success);
        } else {
          const alertMessage = GetMessageExceptionCode(res.data.exception);
          this.context.dispatch(
            "Alert/SetAlertMessage",
            {
              alertMessage: alertMessage,
              typeMessage: TypeComponentNoti.Error,
            },
            { root: true }
          );
          this.context.commit("SetVerificationMerchantLoadingStatus", LoadingStatusType.Fail);
        }
        this.context.dispatch("Loading/IsLoading", false, { root: true });
      });
    }
  }

  @Action
  public DoVerificationMerchantByMatchLink(input: any) {
    if (this.verificationMerchantByMatchLinkLoadingStatus !== LoadingStatusType.Loading) {
      this.context.dispatch("Loading/IsLoading", true, { root: true });
      this.context.commit("SetVerificationMerchantByMatchLinkLoadingStatus", LoadingStatusType.Loading);
      API.MerchantService.DoVerificationMerchantByMatchLink(input).then((res) => {
        if (res.data.responseCode.IsSuccess()) {
          this.context.commit("SetVerificationMerchantByMatchLinkLoadingStatus", LoadingStatusType.Success);
        } else {
          const alertMessage = GetAppConnectErrorMessageByExceptionCode(res.data.exception);
          this.context.dispatch(
            "Alert/SetAlertMessage",
            {
              alertMessage: alertMessage,
              typeMessage: TypeComponentNoti.Error,
            },
            { root: true }
          );
          this.context.commit("SetVerificationMerchantByMatchLinkLoadingStatus", LoadingStatusType.Fail);
        }
        this.context.dispatch("Loading/IsLoading", false, { root: true });
      });
    }
  }
}
